import React, { useEffect, useState } from 'react';
import { 
  Bullseye, 
  Card, 
  CardBody, 
  CardHeader,
  CardTitle,
  Divider,
  Spinner
} from '@patternfly/react-core';
import { fetchData } from '@app/utils/fetchData';

// --- API Types ---
interface DeliveryStats {
  confirmed: number;
  cancelled: number;
  pending: number;
}

interface UpcomingDeliveriesData {
  currentWeek: DeliveryStats;
  nextWeek: DeliveryStats;
  lastWeek: DeliveryStats;
}

export const UpcomingDeliveriesCard: React.FC = () => {
  const [data, setData] = useState<UpcomingDeliveriesData | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    let isMounted = true;

    const loadData = async () => {
      try {
        setIsLoading(true);
        // Fetch the data from the dashboard endpoint
        const response = await fetchData('/dashboard/upcomingdeliveries');
        if (isMounted) {
          setData(response);
        }
      } catch (error) {
        console.error("Failed to load upcoming deliveries:", error);
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadData();

    return () => {
      isMounted = false;
    };
  }, []);

  // Map the API data or default to 0 if data isn't loaded/available
  // Summing confirmed + pending to represent all classes "happening" or "scheduled" this week
  const currentWeekCount = data ? (data.currentWeek.confirmed) : 0;
  const nextWeekConfirmed = data ? data.nextWeek.confirmed : 0;
  const nextWeekWaiting = data ? data.nextWeek.pending : 0;

  return (
    <Card isCompact isFullHeight>
      <CardHeader>
        <CardTitle>Upcoming deliveries</CardTitle>
      </CardHeader>
      
      <CardBody>
        {isLoading ? (
          <Bullseye style={{ minHeight: '120px' }}>
            <Spinner size="lg" aria-label="Loading upcoming deliveries" />
          </Bullseye>
        ) : (
          <>
            {/* Primary Metric: Current Week */}
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', marginTop: '8px', marginBottom: '24px' }}>
              <span 
                style={{ 
                  fontSize: 'var(--pf-t--global--font--size--4xl, 36px)', 
                  fontWeight: 'bold', 
                  lineHeight: 1,
                  color: 'var(--pf-t--global--text--color--regular, #151515)'
                }}
              >
                {currentWeekCount}
              </span>
              <span 
                style={{ 
                  fontSize: 'var(--pf-t--global--font--size--sm, 14px)', 
                  color: 'var(--pf-t--global--text--color--subtle, #6a6e73)' 
                }}
              >
                classes scheduled this week
              </span>
            </div>

            <Divider style={{ marginBottom: '16px' }} />

            {/* Secondary Metrics: Next Week */}
            <div>
              <div style={{ fontSize: '12px', color: 'var(--pf-t--global--text--color--subtle, #6a6e73)', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Next week
              </div>
              
              <div style={{ display: 'flex', gap: '32px', flexWrap: 'wrap' }}>
                {/* Confirmed */}
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <span style={{ fontSize: 'var(--pf-t--global--font--size--xl, 20px)', fontWeight: 'bold' }}>
                    {nextWeekConfirmed}
                  </span>
                  <span style={{ fontSize: '13px', color: 'var(--pf-t--global--text--color--regular, #151515)' }}>
                    Confirmed
                  </span>
                </div>

                {/* Waiting Confirmation */}
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <span style={{ fontSize: 'var(--pf-t--global--font--size--xl, 20px)', fontWeight: 'bold', color: nextWeekWaiting > 0 ? 'var(--pf-t--global--color--status--warning--default, #f0ab00)' : 'inherit' }}>
                    {nextWeekWaiting}
                  </span>
                  <span style={{ fontSize: '13px', color: 'var(--pf-t--global--text--color--regular, #151515)' }}>
                    Waiting confirmation
                  </span>
                </div>
              </div>
            </div>
          </>
        )}
      </CardBody>
    </Card>
  );
};